import React, { useState, useEffect } from 'react'

import { DateInput2 } from '@blueprintjs/datetime2'
import {
  Card,
  NumericInput,
  ControlGroup,
  TextArea,
  Switch,
  Spinner,
  FormGroup,
  Intent,
  Classes,
  Position,
} from '@blueprintjs/core'
import {
  IconButton,
  Icon,
  Flex,
  PrettyDate,
  Details,
  Button,
  Confirmation,
  Text,
  PrettyNumber,
  PrettyDollar,
  GridTableStatusEnum,
  NewGridTable,
  IGridTableColumn,
  IGridTableCell,
  Popover,
} from '@ui'
import { Tooltip2 } from '@blueprintjs/popover2'
import { ISectionProps } from './Details'
import { get, capitalize } from 'lodash'
import moment from 'moment'
import LeaseEventTypeSelect from './EventTypeSelect'
import LeaseUnitTypeSelect from './EventUnitSelect'
import FilesArea from '@ui/Details/Files'
import { ILeaseFullEvent } from '@api'
import css from './leaseManagement.module.scss'

export default function LeaseEvents({
  hook,
  toNotifications,
}: ISectionProps & {
  toNotifications: () => void
}) {
  const [saving, setSaving] = useState<{ [id: string]: boolean }>({})
  const {
    editingEvent,
    addNewEvent,
    updateEvent,
    updateEditEvent,
    cancelEditEvent,
    setLocalEventFiles,
    deleteEvent,
    saveEvent,
    isSavingEvent,
    lease,
    eventErrors,
  } = hook

  const [focused, setFocused] = useState('')
  const [amount, setAmount] = useState<number | string>(0)

  useEffect(() => {
    setAmount(editingEvent.amount)
  }, [editingEvent.amount, setAmount])

  const columns: IGridTableColumn<ILeaseFullEvent>[] = [
    {
      label: 'Type',
      cellStyle: {
        minWidth: 120,
      },
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>placeholder</span>

        const event = (cell as IGridTableCell<ILeaseFullEvent>).data

        if (editingEvent && editingEvent.id === event.id) {
          return (
            <div className="h-full" style={{ display: 'flex', alignContent: 'flex-start' }}>
              <LeaseEventTypeSelect
                buttonProps={{ disabled: isSavingEvent }}
                value={get(editingEvent, 'eventType')}
                onChange={({ value }) => updateEvent('eventType', value)}
              />
            </div>
          )
        }

        return <>{event.eventType}</>
      },
    },
    {
      label: 'Description',
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>description</span>

        const event = (cell as IGridTableCell<ILeaseFullEvent>).data

        if (editingEvent && editingEvent.id === event.id) {
          return (
            <div
              key="description_container"
              className="h-full"
              style={{ display: 'flex', alignContent: 'flex-start' }}
            >
              <FormGroup
                key="description_form"
                helperText={capitalize(eventErrors.description)}
                intent={eventErrors.description ? Intent.DANGER : Intent.NONE}
              >
                <TextArea
                  key="description"
                  intent={eventErrors.description ? Intent.DANGER : Intent.NONE}
                  rows={2}
                  disabled={isSavingEvent}
                  fill
                  onFocus={(e) => {
                    const val = e.target.value
                    e.target.value = ''
                    e.target.value = val

                    setFocused('description')
                  }}
                  autoFocus={focused === 'description' ? true : false}
                  onBlur={() => setFocused('')}
                  value={get(editingEvent, 'description')}
                  onChange={({ target: { value } }) => updateEvent('description', value)}
                />
              </FormGroup>
            </div>
          )
        }

        return (
          <Tooltip2 content={<div style={{ whiteSpace: 'pre-wrap' }}>{event.description}</div>}>
            <Text lines={2}>{event.description}</Text>
          </Tooltip2>
        )
      },
    },
    {
      label: 'Date',
      cellStyle: {
        minWidth: 200,
      },
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>placeholder</span>

        const event = (cell as IGridTableCell<ILeaseFullEvent>).data

        if (editingEvent && editingEvent.id === event.id) {
          return (
            <div className="h-full" style={{ display: 'flex', alignContent: 'flex-start' }}>
              <Flex direction="column">
                <FormGroup
                  helperText={capitalize(eventErrors.date)}
                  intent={eventErrors.date ? Intent.DANGER : Intent.NONE}
                >
                  <DateInput2
                    disabled={isSavingEvent}
                    value={
                      editingEvent.date ? moment(editingEvent.date).toString() : editingEvent.date
                    }
                    onChange={(date: string | null, isUserChange: boolean) => {
                      if (date === null || date.toString() !== 'Invalid Date') {
                        updateEvent('date', date)
                      }
                    }}
                    parseDate={(dateStr) => moment(dateStr, 'MM/DD/YYYY').toDate()}
                    formatDate={(date) => moment(date).format('MMM DD, YYYY')}
                    maxDate={moment().add(10, 'years').toDate()}
                    popoverProps={{ minimal: true }}
                  />
                  {editingEvent.date &&
                    moment(editingEvent.date).isAfter(moment().add(2, 'days')) && (
                      <Tooltip2 content="Enable to get notification email on this date">
                        <Switch
                          disabled={isSavingEvent}
                          style={{ marginTop: 5 }}
                          checked={editingEvent.dateAlert}
                          label={editingEvent.dateAlert ? 'notifications on' : 'notifications off'}
                          onChange={() => {
                            // soft save on change (must click save to submit)
                            updateEvent('dateAlert', !editingEvent.dateAlert)
                          }}
                        />
                      </Tooltip2>
                    )}
                </FormGroup>
              </Flex>
            </div>
          )
        }

        return (
          <Flex align="baseline">
            <PrettyDate value={event.date || undefined} />
            {event.date && moment().add(2, 'days').isBefore(moment(event.date)) && (
              <Flex align="flex-start">
                <Tooltip2 content="Enable to get notification email on this date">
                  <Switch
                    style={{ marginLeft: 5 }}
                    checked={event.dateAlert}
                    onChange={() => {
                      setSaving({
                        ...saving,
                        [event.id]: true,
                      })
                      saveEvent(
                        {
                          ...event,
                          dateAlert: !event.dateAlert,
                        },
                        true,
                        () =>
                          setSaving({
                            ...saving,
                            [event.id]: false,
                          })
                      )
                    }}
                  />
                </Tooltip2>
                {saving[event.id] && <Spinner size={18} />}
              </Flex>
            )}
          </Flex>
        )
      },
    },
    {
      label: 'Amount',
      cellStyle: {
        minWidth: 200,
      },
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>placeholder</span>

        const event = (cell as IGridTableCell<ILeaseFullEvent>).data

        if (editingEvent && editingEvent.id === event.id) {
          return (
            <div className="h-full" style={{ display: 'flex', alignContent: 'flex-start' }}>
              <FormGroup
                helperText={capitalize(eventErrors.amount)}
                intent={eventErrors.amount ? Intent.DANGER : Intent.NONE}
              >
                <ControlGroup>
                  <NumericInput
                    buttonPosition="none"
                    disabled={isSavingEvent}
                    intent={eventErrors.amount ? Intent.DANGER : Intent.NONE}
                    style={{ width: 70, height: 32 }}
                    value={amount}
                    onFocus={(e) => {
                      const val = e.target.value
                      e.target.value = ''
                      e.target.value = val

                      setFocused('amount')
                    }}
                    autoFocus={focused === 'amount' ? true : false}
                    onBlur={() => {
                      setFocused('')
                      updateEvent('amount', amount)
                      setAmount(+amount)
                    }}
                    onValueChange={(value, stringVal) => {
                      const isDotIncluded = stringVal.includes('.')

                      setAmount((amount) => {
                        if (isDotIncluded) {
                          return value ? stringVal : +amount
                        }

                        return value ?? 0
                      })
                    }}
                    onKeyPress={(e) => {
                      const val = e.currentTarget.value
                      if (val.includes('.') && !val.match(/^\d+(?:.\d{0,1})?$/)) {
                        e.preventDefault()
                      }
                    }}
                  />
                  <LeaseUnitTypeSelect
                    buttonProps={{
                      disabled: isSavingEvent,
                      style: {
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                      },
                    }}
                    value={editingEvent.units}
                    onChange={({ value }) => updateEvent('units', value)}
                  />
                </ControlGroup>
              </FormGroup>
            </div>
          )
        }

        return event.units === '$' ? (
          <PrettyDollar value={event.amount} />
        ) : (
          <Flex style={{ whiteSpace: 'pre-wrap' }}>
            <PrettyNumber value={event.amount} />
            {` ${event.units}`}
          </Flex>
        )
      },
    },
    {
      label: 'Files',
      Cell: ({ cell }) => {
        if (!cell) return <span className={Classes.SKELETON}>placeholder</span>

        const event = (cell as IGridTableCell<ILeaseFullEvent>).data

        if (editingEvent && editingEvent.id === event.id) {
          return (
            <div className="h-full" style={{ display: 'flex', alignContent: 'flex-start' }}>
              <Popover
                minimal
                position={Position.LEFT}
                content={
                  <FilesArea
                    containerStyle={{
                      marginTop: 0,
                      width: '100%',
                      minWidth: 300,
                      minHeight: 180,
                      maxHeight: 500,
                      overflowY: 'scroll',
                    }}
                    editing={true}
                    files={event.files}
                    localFiles={hook.localEventFiles}
                    onAdd={(newFiles) => {
                      setLocalEventFiles([...newFiles, ...hook.localEventFiles])
                    }}
                    onRemove={(file) => hook.deleteEventAsset(event, file)}
                  />
                }
              >
                <IconButton
                  disabled={isSavingEvent}
                  iconName={event.files.length ? 'file' : 'file-upload'}
                  minimal
                  text={
                    !event.files.length && !hook.localEventFiles.length
                      ? undefined
                      : event.files.length + hook.localEventFiles.length
                  }
                />
              </Popover>
            </div>
          )
        }

        return event.files && event.files.length ? (
          <Popover
            minimal
            position={Position.LEFT}
            content={
              <FilesArea
                containerStyle={{
                  marginTop: 0,
                  width: '100%',
                  minWidth: 100,
                  minHeight: 66,
                  maxHeight: 500,
                  overflowY: 'scroll',
                }}
                editing={false}
                files={event.files}
                localFiles={hook.localEventFiles}
                onAdd={(newFiles) => {
                  setLocalEventFiles([...newFiles, ...hook.localEventFiles])
                }}
                onRemove={(file) => hook.deleteEventAsset(event, file)}
              />
            }
          >
            <IconButton iconName="file" minimal text={event.files.length} />
          </Popover>
        ) : (
          <>-</>
        )
      },
    },
    {
      label: 'Created By/At',
      Cell: ({ cell, isHighlighted }) => {
        if (!cell) return <span className={Classes.SKELETON}>placeholder</span>

        const event = (cell as IGridTableCell<ILeaseFullEvent>).data

        if (editingEvent && editingEvent.id === event.id) {
          return (
            <Flex align="flex-start">
              <IconButton
                disabled={isSavingEvent}
                style={{ marginRight: 10 }}
                iconName="times"
                round
                tooltip="cancel"
                minimal
                onClick={() => cancelEditEvent()}
              />
              <IconButton
                loading={isSavingEvent}
                iconName="check"
                round
                tooltip="save"
                minimal
                onClick={() => saveEvent(event, false)}
              />
            </Flex>
          )
        }

        return isHighlighted ? (
          <Flex style={{ height: 36, width: 100 }}>
            <IconButton
              iconName="pencil"
              round
              tooltip="edit"
              minimal
              onClick={() => updateEditEvent(event.id)}
            />
            <Confirmation text="Delete this Event?" onConfirm={() => deleteEvent(event.id)}>
              <IconButton
                style={{ marginLeft: 10 }}
                iconName="trash"
                round
                tooltip="delete"
                minimal
              />
            </Confirmation>
          </Flex>
        ) : (
          <Flex direction="column">
            <span className={css.single_line}>{event.createdBy?.name}</span>
            <span className={css.single_line}>
              <PrettyDate value={event.createdAt} />
            </span>
          </Flex>
        )
      },
    },
  ]

  const format = (items: ILeaseFullEvent[]) =>
    items.map((i) => ({
      data: i,
    }))

  const formated = format(lease ? lease.contractEvents : [])

  return (
    <div style={{ width: '100%' }}>
      <Details.Header
        label="Lease Events"
        iconName="list-ul"
        leftItem={
          <Flex align="center">
            <Tooltip2
              content={
                <div style={{ maxWidth: 450 }}>
                  Record your important lease milestones here for future reference. Lease events can
                  be renewals, subleases, terminations, or other events. You can attach reference
                  documents to each event as needed.
                </div>
              }
            >
              <Icon
                style={{
                  color: '#999',
                  fontSize: 18,
                  margin: '15px 0 10px 5px',
                }}
                name="info-circle"
              />
            </Tooltip2>
            <div className={css.link} style={{ marginLeft: 20 }} onClick={toNotifications}>
              set notification recipients
            </div>
          </Flex>
        }
        rightItem={
          <Button
            ghost
            text="New Event"
            iconName="plus"
            disabled={hook.isCreatingEvent}
            onClick={() => addNewEvent()}
          />
        }
      />
      <Card style={{ padding: 10 }}>
        <NewGridTable<ILeaseFullEvent>
          key="lease_events_table"
          status={!lease ? GridTableStatusEnum.LOADING : GridTableStatusEnum.IDLE}
          minCellWidth="auto"
          columns={columns}
          data={formated}
          noDataMessage="No Events Yet"
        />
      </Card>
    </div>
  )
}
